// The API's error envelope: {"error": {"code", "message", "details"}}.
export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown> & { fields?: { field: string; code: string }[] };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ApiErrorBody["details"];
  readonly requestId: string | null;
  readonly retryAfter: number | null;

  constructor(status: number, body: ApiErrorBody | null, requestId: string | null, retryAfter: number | null) {
    super(body?.message ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code ?? (status === 0 ? "network_error" : "unknown_error");
    this.details = body?.details;
    this.requestId = requestId;
    this.retryAfter = retryAfter;
  }

  /** Field errors from a 422 validation_failed, keyed by field name. */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.details?.fields ?? []).map((f) => [f.field, f.code]));
  }
}

/** Short, human copy for the codes the Hub surfaces most (COPY-AND-STATES). */
export function describeError(err: unknown): string {
  if (!(err instanceof ApiError)) return "Something went wrong. Try again.";
  switch (err.code) {
    case "network_error":
      return "Can't reach Substratal. Check your connection and try again.";
    case "invalid_credentials":
      return "That email and password don't match. Try again or reset your password.";
    case "too_many_attempts":
      return "Too many attempts. Wait 15 minutes, then try again.";
    case "account_suspended":
      return "This account is suspended. Contact your administrator.";
    case "test_user_expired":
      return "This test account has expired.";
    case "invalid_mfa_code":
      return "That code didn't work. Check your authenticator app and try again.";
    case "mfa_token_invalid":
      return "That sign-in attempt expired. Start again.";
    case "rate_limited":
      return err.retryAfter ? `Too many requests. Try again in ${err.retryAfter} seconds.` : "Too many requests. Try again shortly.";
    case "forbidden":
      return "You don't have permission to do that.";
    case "unauthenticated":
    case "session_revoked":
    case "refresh_token_reused":
      return "Your session ended. Sign in again.";
    case "api_version_required":
    case "unsupported_api_version":
      return "This version of the Hub is out of date. Reload the page to update it.";
    case "token_invalid":
      return "This link has expired or was already used. Ask for a new one.";
    case "weak_password":
      return describePasswordProblem(err.details?.reason);
    case "email_taken":
      return "An account already uses that email. Sign in, or reset your password.";
    default:
      return err.message;
  }
}

function describePasswordProblem(reason: unknown): string {
  switch (reason) {
    case "too_short":
      return "Use at least 12 characters.";
    case "too_long":
      return "Use 128 characters or fewer.";
    case "breached":
      return "That password appears in known data breaches. Choose a different one.";
    case "matches_email":
      return "Your password can't be your email address.";
    default:
      return "Choose a different password.";
  }
}
